import * as React from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { documentsTableStore, UNDOWNLOADABLE } from "../Stores/DocumentsTableStore";
import { I18N_NS } from "Config/i18n";
import {
  DetailListActions,
  PrimaryActionsConfig,
  GroupedDropdownActionConfig,
} from "Components/General/DetailListActions";
import { ShowModalFunction } from "Components/General/Modal/ModalContext";
import { EmailSendModal } from "Pages/Project/Communication/EmailSendModal/EmailSendModal";
import { emailSendModal } from "Pages/Project/Communication/utils/emailModalProps";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { MoveFilesForm, MoveFilesFormProps } from "./MoveFilesForm";
import { projectsDataStore } from "Stores/ProjectsStore";
import { observer } from "mobx-react";
import { QANewQuestion } from "Pages/Project/QA/Components/QANewQuestion";
import { collectionFolderStore } from "../CollectionFolder/CollectionFolderStore";
import { printJobSettingsStore } from "Pages/Project/Settings/Stores/PrintJobStore";
import { CollectionFolderFile } from "../CollectionFolder/interfaces";
import { documentsSearchStore } from "../Stores/DocumentsSearchStore";
import { findFileById } from "../utils/documentsItems";
import { StartWorkflowWithFiles } from "../CollectionFolder/StartWorkflowWithFiles";
import { fileDownloadStore } from "../FileDownload/FileDownloadStore";
import { taskModalStore } from "Stores";
import { taskAttachmentsStore } from "Pages/Project/Tasks/Stores/TaskAttachmentsStore";
import { LabelCalculator } from "../Calculator/LabelCalculator";
import { labelCalculatorStore } from "../Calculator/LabelCalculatorStore";
import { RestoreModal } from "./RestoreModal";
import { PrintForm } from "Components/FilePrint/Components/PrintForm";
import { modal } from "Components/General/Modal/AntdModal";
import { useCallback, useMemo } from "react";
import { useEmailAttachments } from "utils/hooks/useEmailAttachments";
import { emailSubmitStore } from "Pages/Project/Communication/Stores/EmailSubmitStore";
import { SaveQueryForm, SaveQueryFormProps } from "./Search/SaveQueryForm";
import { RouterStore } from "Config/CustomBrowserRouter";
import { getUrlParams } from "utils/getUrlParams";
import { ChangeFilesOwnerForm } from "./ChangeFilesOwnerForm";

const COMPARE_ONLY_2 = "COMPARE_ONLY_2";
const UNCOMPARABLE = "UNCOMPARABLE";

interface DocumentsActionsMenuProps {
  files: (API_FileDataEntity & API_ExternalLinked)[];
  showOnlyDropdown?: boolean;
  showModal?: ShowModalFunction;
  resetSelection: () => void;
  currentFolderId?: number;
  isCollectionFolder?: boolean;
  onAfterActionFinished?: () => void;
  isSearch?: boolean;
}

interface DisabledActions extends API_FileActions, Pick<API_FileDataEntity, "malware" | "unread"> {
  exportExcel?: boolean;
  labels?: boolean;
  read?: boolean;
}

export type DocumentsFileActionType =
  | "download"
  | "move"
  | "create_symlink"
  | "delete"
  | "delete_link"
  | "print"
  | "send"
  | "compare"
  | "ask_q"
  | "to_collection"
  | "from_collection"
  | "download_with_dirs"
  | "download_pdf"
  | "export_excel"
  | "download_pdf_with_qr"
  | "start_workflow"
  | "task"
  | "labels"
  | "restore"
  | "read"
  | "unread"
  | "change_owner";

export const DocumentsActionsMenu = observer(
  ({
    files,
    isSearch,
    showOnlyDropdown = false,
    showModal,
    resetSelection,
    currentFolderId,
    isCollectionFolder = false,
    onAfterActionFinished,
  }: DocumentsActionsMenuProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const { checkAttachmentsAccess } = useEmailAttachments(emailSubmitStore);

    const handleDelete = useCallback(
      (deleteType: "delete" | "delete_link") => {
        let title: string, content: string;
        if (deleteType === "delete") {
          title = t("Mark as deleted");
          content = t("Really mark as deleted?");
        } else if (deleteType === "delete_link") {
          title = t("Delete link");
          content = t("Do you really want to delete the link?");
        }
        modal.confirm({
          title: title,
          content: content,
          okText: t("Yes"),
          cancelText: t("No"),
          onOk: () => {
            documentsTableStore
              .deleteFiles(files)
              .then(resetSelection)
              .then(() => {
                if (onAfterActionFinished) {
                  onAfterActionFinished();
                }
              });
          },
        });
      },
      [files, onAfterActionFinished, resetSelection]
    );

    const handleMenuClick = useCallback(
      async ({ key }: { key: DocumentsFileActionType }) => {
        const isAbletoDownload = files.some((file) => file.fileActions?.download);
        const isAbletoDownloadWithWatermark = files.some((file) => file.fileActions?.downloadWithWatermark);

        if (!files.length) {
          message.error(t("No files were selected"));
          return;
        }

        let action: () => Promise<void>;
        const fileIds = files.map((item) => item.id);

        switch (key) {
          case "download": {
            const filesToDownload = files.map((item) => {
              if (item.state === "SYMLINK") {
                return { id: item.linkId, state: item.state };
              } else if (item.state === "EXTERN_SYMLINK") {
                return { id: item.id, state: item.state, linkId: item.linkId };
              } else {
                return { id: item.id, state: item.state };
              }
            });
            action = () =>
              fileDownloadStore.initModal(
                filesToDownload,
                !isAbletoDownload && isAbletoDownloadWithWatermark ? "RWM" : "ALL"
              );
            break;
          }
          case "download_with_dirs":
            action = () => collectionFolderStore.downloadSelection(fileIds, true);
            break;
          case "download_pdf":
            action = () => documentsTableStore.downloadFilesPdfAsZip(fileIds);
            break;
          case "download_pdf_with_qr":
            action = () => documentsTableStore.downloadFilesPdfWithQRasZip(fileIds);
            break;
          case "task":
            action = () =>
              new Promise((res) => {
                files.forEach((file) => {
                  taskAttachmentsStore.addProjectFile(file);
                });
                taskModalStore.toggleModal("docs");
                res();
              });
            break;
          case "to_collection":
            action = () =>
              new Promise((res) => {
                collectionFolderStore.addFiles(files as CollectionFolderFile[]);
                res();
              });
            break;
          case "from_collection":
            action = async () => {
              files.forEach((f) => collectionFolderStore.removeFile(f.id));
              resetSelection();
            };
            break;
          case "move":
            action = () =>
              new Promise((res) => {
                showModal<Partial<MoveFilesFormProps>>(MoveFilesForm, {
                  modalProps: { ...modalProps, title: t("Move files") },
                  componentProps: {
                    files: files,
                    onSubmitSuccess: () => documentsTableStore.refresh(),
                    defaultFolderId: documentsTableStore.currentFolderId,
                    onMoveFile: (files, folderId) =>
                      documentsTableStore.moveFiles(files, folderId).then(() => {
                        if (onAfterActionFinished) {
                          onAfterActionFinished();
                        }
                      }),
                  },
                });
                res();
              });
            break;
          case "create_symlink":
            action = () =>
              new Promise((res) => {
                showModal<Partial<MoveFilesFormProps>>(MoveFilesForm, {
                  modalProps: { ...modalProps, title: t("Create link") },
                  componentProps: {
                    files,
                    onSubmitSuccess: () => documentsTableStore.refresh(),
                    defaultFolderId: documentsTableStore.currentFolderId,
                    onLinkFile: (files, folderId) =>
                      documentsTableStore.createFileLink(files, folderId).then(() => {
                        if (onAfterActionFinished) {
                          onAfterActionFinished();
                        }
                      }),
                  },
                });
                res();
              });
            break;
          case "restore":
            action = () =>
              new Promise((res) => {
                showModal(RestoreModal, {
                  modalProps: { ...modalProps, title: t("Restore") },
                  componentProps: {
                    files: files,
                    restoreFiles: (...args) =>
                      documentsTableStore.restoreFiles(...args).then(() => {
                        if (onAfterActionFinished) {
                          onAfterActionFinished();
                        }
                      }),
                  },
                });
                res();
              });
            break;
          case "read":
            action = () =>
              documentsTableStore.setFilesReadStatus(fileIds, false).then(() => {
                if (onAfterActionFinished) {
                  onAfterActionFinished();
                }
              });
            break;
          case "unread":
            action = () =>
              documentsTableStore.setFilesReadStatus(fileIds, true).then(() => {
                if (onAfterActionFinished) {
                  onAfterActionFinished();
                }
              });
            break;
          case "delete":
            action = () =>
              new Promise((res) => {
                handleDelete("delete");
                res();
              });
            break;
          case "delete_link":
            action = () =>
              new Promise((res) => {
                handleDelete("delete_link");
                res();
              });
            break;
          case "print":
            action = () =>
              new Promise((res) => {
                const filesToPrint = fileIds.reduce((allFiles, currentFileId) => {
                  const fileFound = files.find((item) => item.id === currentFileId);
                  if (fileFound) {
                    allFiles.push(fileFound);
                  } else {
                    console.warn(`File ID ${currentFileId} not found in Actions menu props.`);
                  }
                  return allFiles;
                }, []);
                showModal(PrintForm, {
                  modalProps: { style: { maxWidth: 1300, top: 20 }, width: "100%", footer: null, title: " " },
                  componentProps: {
                    files: filesToPrint,
                  },
                });
                res();
              });
            break;
          case "send":
            action = () =>
              new Promise((res) => {
                checkAttachmentsAccess(files);
                showModal(EmailSendModal, {
                  modalProps: { ...emailSendModal },
                  componentProps: {
                    mailType: "attach",
                  },
                });
                res();
              });
            break;
          case "ask_q":
            action = () =>
              new Promise((res) => {
                showModal(QANewQuestion, {
                  modalProps: { ...modalProps, title: t("New question") },
                  componentProps: { attachments: files },
                });
                res();
              });
            break;
          case "export_excel":
            action = () => documentsSearchStore.downloadExcel(files);
            break;
          case "start_workflow":
            action = () =>
              new Promise((res) => {
                showModal(StartWorkflowWithFiles, {
                  modalProps: { ...modalProps, title: t("Start workflow") },
                  componentProps: { fileIds: fileIds },
                });
                res();
              });
            break;
          case "labels":
            action = () =>
              new Promise((res) => {
                labelCalculatorStore.setMode("FILE");
                showModal(LabelCalculator, {
                  modalProps: {
                    title: " ",
                    width: "100%",
                    style: { maxWidth: 1200 },
                    footer: null,
                  },
                  componentProps: { files },
                });
                res();
              });
            break;
          case "change_owner":
            action = () =>
              new Promise((res) => {
                showModal(ChangeFilesOwnerForm, {
                  modalProps: { ...modalProps, title: t("Change file owner") },
                  componentProps: {
                    fileIds: files.map((file) => file.id),
                    onSubmitSuccess: () => {
                      if (onAfterActionFinished) {
                        onAfterActionFinished();
                      }
                      documentsTableStore.refresh();
                    },
                  },
                });

                res();
              });
            break;
          default:
            action = () => {
              message.warning(t("Invalid action"));
              return Promise.resolve();
            };
            break;
        }

        const hide = message.loading(t("Action in progress..."), 0);
        try {
          await action();
        } catch (error) {
          if (error === COMPARE_ONLY_2) {
            message.error(t("Please select exactly two files for comparisons."));
          } else if (error === UNCOMPARABLE) {
            message.error(t("Please select two dwg or pdf files."));
          } else if (error.message === UNDOWNLOADABLE) {
            message.error(t("Some of the selected files are not allowed to be downloaded."));
          } else {
            throw error;
          }
        } finally {
          hide();
        }
      },
      [files, handleDelete, onAfterActionFinished, resetSelection, showModal]
    );

    const handleDropToCollectionFolder = useCallback((e: React.DragEvent, search: boolean = false) => {
      e.preventDefault();
      const store = search ? documentsSearchStore : documentsTableStore;
      const rowData = JSON.parse(e.dataTransfer.getData("text"));
      const fileData = findFileById(store.data, rowData.id);
      if (fileData) {
        collectionFolderStore.addFile(fileData);
      }
    }, []);

    const getDisabledMessage = useCallback((isMalware: boolean, isDisabled: boolean) => {
      if (isMalware) {
        return t("One file is infected by a virus! Please remove it from your selection.");
      }
      if (isDisabled) {
        return t("For one or more selected files this action is not allowed.");
      }
      return null;
    }, []);

    const primarySearchActions = useMemo<PrimaryActionsConfig[]>(() => {
      return [
        {
          onClick: () => {
            const { folderId } = getUrlParams(RouterStore.location.search);
            documentsSearchStore.reset();
            if (folderId) {
              documentsTableStore.goToFolder(+folderId);
            } else {
              RouterStore.push(`/project/${projectsDataStore.projectId}/documents/docs`);
            }
          },
          icon: ["fas", "chevron-left"],
          label: t("Back"),
        },
        {
          onClick: () =>
            showModal<Partial<SaveQueryFormProps>>(SaveQueryForm, {
              modalProps: { ...modalProps, title: t("Save query") },
            }),
          icon: ["far", "star"],
          label: t("Save query") + "...",
        },
        {
          label: t("Collection folder"),
          onClick: collectionFolderStore.toggleModal,
          onDrop: (e) => handleDropToCollectionFolder(e, true),
          badgeCount: collectionFolderStore.fileIds.length,
          icon: "folder-open",
        },
      ];
    }, [handleDropToCollectionFolder, showModal, collectionFolderStore.fileIds.length]);

    const primaryActions = useMemo<PrimaryActionsConfig[]>(() => {
      if (showOnlyDropdown) {
        return [];
      }

      if (isSearch) {
        return primarySearchActions;
      }

      return [
        {
          label: t("Up"),
          onClick: () => {
            const folderId = documentsTableStore.uplevelFolderId;
            documentsTableStore.clearFilters();
            documentsTableStore.goToFolder(folderId);
          },
          icon: "chevron-up",
        },
        {
          label: t("Refresh"),
          onClick: () => {
            resetSelection();
            documentsTableStore.refresh();
          },
          icon: "sync",
        },
        {
          label: t("Collection folder"),
          onDrop: (e) => handleDropToCollectionFolder(e),
          onClick: collectionFolderStore.toggleModal,
          badgeCount: collectionFolderStore.fileIds.length,
          icon: "folder-open",
        },
      ];
    }, [handleDropToCollectionFolder, resetSelection, showOnlyDropdown, collectionFolderStore.fileIds.length]);

    const dropdownActions = useMemo<GroupedDropdownActionConfig>(() => {
      const disabledActions: DisabledActions = {};
      const isAbletoDownload = files.every((file) => file.fileActions?.download);
      const isAbletoDownloadWithWatermark = files.every((file) => file.fileActions?.downloadWithWatermark);

      const nonSymlinks = files.some((file) => file.state !== "EXTERN_SYMLINK");
      const extSymlinks = new Set(files.filter((file) => file.state === "EXTERN_SYMLINK").map((f) => f.linkId));
      const mixedExternal = (nonSymlinks && extSymlinks.size > 0) || extSymlinks.size > 1;

      let firstLabelFingerprint: string;

      files.forEach(({ fileActions = {}, malware, type, unread, labelFingerprint }, index) => {
        if (index === 0) {
          firstLabelFingerprint = labelFingerprint;
        }
        if (!Object.keys(fileActions).length) {
          disabledActions.exportExcel = true;
        }
        if (malware) {
          disabledActions.malware = true;
          return;
        }
        if (!fileActions.download) {
          disabledActions.download = true;
        }
        if (!fileActions.changeFileOwner) {
          disabledActions.changeFileOwner = true;
        }
        if (!isAbletoDownload && isAbletoDownloadWithWatermark) {
          if (fileActions.downloadPdf) {
            disabledActions.downloadPdf = false;
          } else if (type.toLowerCase() !== "pdf") {
            disabledActions.downloadPdf = true;
          }
        } else if (!fileActions.downloadPdf) {
          disabledActions.downloadPdf = true;
        }
        if (!fileActions.downloadPdfWithQRAsZip) {
          disabledActions.downloadPdfWithQRAsZip = true;
        }
        if (!fileActions.collectionFolder) {
          disabledActions.collectionFolder = true;
        }
        if (!fileActions.addToTask) {
          disabledActions.addToTask = true;
        }
        if (!fileActions.move) {
          disabledActions.move = true;
        }
        if (!fileActions.delete) {
          disabledActions.delete = true;
        }
        if (!fileActions.send) {
          disabledActions.send = true;
        }
        if (!fileActions.askQuestion) {
          disabledActions.askQuestion = true;
        }
        if (!fileActions.print) {
          disabledActions.print = true;
        }
        if (!fileActions.workflow) {
          disabledActions.workflow = true;
        }
        if (!disabledActions.labels && firstLabelFingerprint !== labelFingerprint) {
          disabledActions.labels = true;
        }
        if (!fileActions.editLabels) {
          disabledActions.labels = true;
        }
        if (!fileActions.restore) {
          disabledActions.restore = true;
        }
        if (unread === undefined) {
          disabledActions.unread = true;
          disabledActions.read = true;
        } else {
          if (unread) {
            disabledActions.unread = true;
          } else {
            disabledActions.read = true;
          }
        }
      });

      if (extSymlinks.size > 0) {
        disabledActions.unread = true;
        disabledActions.read = true;
      }

      const config: GroupedDropdownActionConfig = {};
      let namespace = t("Multiple files");

      config[namespace] = [
        {
          label: t("Export as Excel"),
          onClick: () => handleMenuClick({ key: "export_excel" }),
          icon: ["far", "file-excel"],
          disabled: disabledActions.exportExcel || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.exportExcel),
        },
      ];

      namespace = "Basics";
      let disabledWatermark = disabledActions.malware || mixedExternal;
      if (!disabledWatermark) {
        if (disabledActions.download && disabledActions.downloadPdf) {
          disabledWatermark = !isAbletoDownloadWithWatermark;
        }
      }

      config[namespace] = [
        {
          label: t("Download") + "...",
          onClick: () => handleMenuClick({ key: "download" }),
          icon: ["far", "download"],
          disabled: disabledWatermark,
          message: getDisabledMessage(disabledActions.malware, disabledWatermark),
        },
        isCollectionFolder
          ? {
              label: t("Remove from collection folder"),
              onClick: () => handleMenuClick({ key: "from_collection" }),
              icon: ["far", "trash-alt"],
            }
          : {
              label: t("Add to collection folder"),
              onClick: () => handleMenuClick({ key: "to_collection" }),
              icon: ["far", "folder-open"],
              disabled: disabledActions.collectionFolder || disabledActions.malware,
              message: getDisabledMessage(disabledActions.malware, disabledActions.collectionFolder),
            },
        {
          label: t("Move") + "...",
          onClick: () => handleMenuClick({ key: "move" }),
          icon: ["far", "dolly"],
          disabled: disabledActions.move || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.move),
        },
        {
          label: t("Create link") + "...",
          onClick: () => handleMenuClick({ key: "create_symlink" }),
          icon: ["far", "link"],
          disabled: disabledActions.move || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.move),
        },
      ];

      if (projectsDataStore.isAdmin) {
        config[namespace].push({
          label: t("Change file owner") + "...",
          onClick: () => handleMenuClick({ key: "change_owner" }),
          icon: ["far", "user"],
          disabled: disabledActions.changeFileOwner || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.changeFileOwner),
        });
      }
      if (projectsDataStore.functions?.includes("FILE_LABELS")) {
        config[namespace].push({
          label: t("Labels") + "...",
          onClick: () => handleMenuClick({ key: "labels" }),
          icon: ["far", "tag"],
          disabled: files.length < 1 ? false : disabledActions.labels || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.labels),
        });
      }
      config[namespace].push(
        {
          label: t("Restore"),
          onClick: () => handleMenuClick({ key: "restore" }),
          icon: ["far", "trash-restore-alt"],
          disabled: disabledActions.restore || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.restore),
        },
        {
          label: t("Mark as read"),
          onClick: () => handleMenuClick({ key: "read" }),
          icon: ["fal", "bold"],
          disabled: disabledActions.read || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.read),
        },
        {
          label: t("Mark as unread"),
          onClick: () => handleMenuClick({ key: "unread" }),
          icon: ["fas", "bold"],
          disabled: disabledActions.unread || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.unread),
        },
        {
          label: t("Mark as deleted"),
          onClick: () => handleMenuClick({ key: "delete" }),
          icon: ["far", "trash-alt"],
          disabled: disabledActions.delete,
          message: getDisabledMessage(disabledActions.malware, disabledActions.delete),
        }
      );

      namespace = t("Printing and other modules");
      config[namespace] = [];
      if (printJobSettingsStore.allowPrint) {
        config[namespace].push({
          label: t("Print job") + "...",
          onClick: () => handleMenuClick({ key: "print" }),
          icon: ["far", "print"],
          disabled: disabledActions.print || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.print),
        });
      }
      if (projectsDataStore.currentModules.includes("MAIL")) {
        config[namespace].push({
          label: t("Send") + "...",
          onClick: () => handleMenuClick({ key: "send" }),
          icon: ["far", "envelope"],
          disabled: disabledActions.send || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.send),
        });
      }

      config[namespace].push({
        label: t("Create a task") + "...",
        onClick: () => handleMenuClick({ key: "task" }),
        icon: ["far", "tasks"],
        disabled: disabledActions.addToTask || disabledActions.malware,
        message: getDisabledMessage(disabledActions.malware, disabledActions.addToTask),
      });

      if (projectsDataStore.currentModules.includes("QA")) {
        config[namespace].push({
          label: t("Ask a question") + "...",
          onClick: () => handleMenuClick({ key: "ask_q" }),
          icon: ["far", "comments"],
          disabled: disabledActions.askQuestion || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.askQuestion),
        });
      }

      if (projectsDataStore.currentModules.includes("WORKFLOW")) {
        config[namespace].push({
          label: t("Start workflow") + "...",
          onClick: () => handleMenuClick({ key: "start_workflow" }),
          icon: ["far", "code-branch"],
          disabled: disabledActions.workflow || disabledActions.malware,
          message: getDisabledMessage(disabledActions.malware, disabledActions.workflow),
        });
      }

      return config;
    }, [files, getDisabledMessage, handleMenuClick, isCollectionFolder]);

    return (
      <DetailListActions
        allowMessagesOnDisabled={true}
        dropdownFirst={true}
        primaryActions={primaryActions}
        groupedDropdownActions={dropdownActions}
      />
    );
  }
);
